import { useState } from "react";
import { Clock, Warehouse, AlertTriangle, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

interface LiquidationSaleDisplayProps {
  totalInventory: number;
  currentInventory: number;
  thresholdPercent: number;
  endDate?: string | null;
  deliveryDays?: number;
  status?: string;
  measurementUnit: string;
  className?: string;
}

export const LiquidationSaleDisplay = ({
  totalInventory,
  currentInventory,
  thresholdPercent,
  endDate,
  deliveryDays = 30,
  status,
  measurementUnit,
  className
}: LiquidationSaleDisplayProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Calculate sold quantity and progress
  const soldQuantity = totalInventory - currentInventory;
  const thresholdQuantity = Math.ceil((totalInventory * thresholdPercent) / 100);
  const progressPercent = Math.min((soldQuantity / thresholdQuantity) * 100, 100);
  const isThresholdReached = progressPercent >= 100 || status === 'threshold_reached';

  const daysLeft = endDate
    ? Math.ceil((new Date(endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const formatUnit = (unit: string) => {
    const units: Record<string, string> = {
      'square_meter': 'מ"ר',
      'meter': 'מטר',
      'unit': 'יח\'',
      'cubic_meter': 'מ"ק',
      'kilogram': 'ק"ג',
      'liter': 'ליטר',
      'package_by_meter': 'מטר',
      'package_by_square_meter': 'מ"ר',
      'package': 'חבילות'
    };
    return units[unit] || unit;
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Main Status Card */}
      <Card className={cn("p-6 space-y-4 bg-white border-2 border-red-200", className)}>
        <div className="flex items-center justify-between border-b border-red-100 pb-4">
          <div className="flex items-center gap-2">
            <Warehouse className="w-5 h-5 text-red-600" />
            <h3 className="font-bold text-lg text-red-900">חיסול מלאי</h3>
          </div>

          {daysLeft !== null && daysLeft >= 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>{daysLeft > 0 ? `${daysLeft} ימים נותרו` : 'מסתיים היום'}</span>
            </div>
          )}
        </div>

        <div className="space-y-6 pt-2">
          {/* Progress Bar Container with Marker */}
          <div className="relative">
            <div className="flex items-center justify-between text-sm mb-2" dir="rtl">
              <span className="text-muted-foreground font-medium">נמכרו: {soldQuantity} {formatUnit(measurementUnit)}</span>
              <span className="font-bold text-red-700">{totalInventory} {formatUnit(measurementUnit)}</span>
            </div>

            {/* Track (Unfilled part) - Light red background with border */}
            <div className="relative h-5 w-full bg-red-50 border border-red-100 rounded-full overflow-visible">
              {/* Main Progress Bar (Filled part) - Anchored to RIGHT */}
              <div
                className="absolute right-0 top-0 bottom-0 bg-slate-800 rounded-full transition-all duration-500 ease-out z-10"
                style={{ width: `${(soldQuantity / totalInventory) * 100}%` }}
              />

              {/* Threshold Marker - Positioned from RIGHT */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20 flex flex-col items-center overflow-visible"
                style={{ right: `${thresholdPercent}%` }}
              >
                {/* Marker Line */}
                <div className="w-0.5 h-7 -mt-1 bg-red-600"></div>

                {/* Text Label for Threshold */}
                <div className="absolute top-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-bold text-red-600 bg-white/80 px-1 rounded shadow-sm border border-red-100 mt-1">
                  יעד למשלוח: {thresholdQuantity}
                </div>
              </div>
            </div>

            {/* Status Text Below */}
            <div className="mt-10 text-center">
              <p className={cn(
                "text-sm font-semibold",
                isThresholdReached ? "text-amber-600" : "text-muted-foreground"
              )}>
                {isThresholdReached
                  ? `היעד הושג! המשלוחים יוצאים בעוד ${deliveryDays || 7} ימים`
                  : `${progressPercent.toFixed(0)}% הושלמו עד כה`
                }
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Details Card */}
      <Card className="border border-red-100 shadow-sm overflow-hidden">
        <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full flex items-center justify-between p-4 h-auto hover:bg-red-50/50 hover:text-red-700 rounded-none group"
            >
              <span className="text-sm font-medium text-muted-foreground group-hover:text-red-700 transition-colors">
                {isOpen ? 'הסתר פרטים' : 'מהו חיסול מלאי? / פרטים מלאים'}
              </span>
              <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:text-red-700", isOpen && "rotate-180")} />
            </Button>
          </CollapsibleTrigger>

          <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
            <div className="p-4 pt-0 space-y-4 bg-red-50/30">
              {/* Detailed Stats */}
              <div className="pt-3 border-t border-red-100/50 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">מלאי כולל:</span>
                  <span className="font-medium">{totalInventory} {formatUnit(measurementUnit)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">נמכרו עד כה:</span>
                  <span className="font-medium text-lime">{soldQuantity} {formatUnit(measurementUnit)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">נותרו במלאי:</span>
                  <span className="font-medium">{currentInventory} {formatUnit(measurementUnit)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">יעד למשלוח:</span>
                  <span className="font-medium">{thresholdQuantity} {formatUnit(measurementUnit)} ({thresholdPercent}%)</span>
                </div>
              </div>

              {/* Explanation */}
              <div className="text-sm space-y-3">
                <p className="font-medium text-foreground">מהו חיסול מלאי?</p>
                <p className="text-muted-foreground leading-relaxed">
                  מלאי שנמכר <span className="font-bold">בפחות</span> מהמחיר שהיבואן מביא את הסחורה. כדי להיפטר ממנו ולפנות שטח אחסון,
                  עלינו לרכוש את כל המלאי שנותר ועל כן יש יעד מינימלי לכמות רכישה כוללת כדי שנמשוך את הסחורה.
                </p>
                <div className="space-y-1 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>המוצר יסופק רק לאחר הגעה ליעד המכירות</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>אספקה משוערת: {deliveryDays || 7} ימים לאחר הגעה ליעד</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>החזר מלא אם לא נגיע ליעד</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>המוצר הינו מוצר חיסול ועל כן לא ניתן להחזירו</span>
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  );
};
